import { useInfiniteQuery } from "@tanstack/react-query";
import axios from "axios";
import { useState, useMemo } from "react";
import { Accordion, Badge, Card, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faUser, faCalendar, faStore, faEdit, faFileImport, faCode, faTags, faBalanceScale } from "@fortawesome/free-solid-svg-icons";
import { useEmpresas } from "providers/EmpresaProvider";
import { useBreakpoints } from "providers/BreakpointsProvider";
import { FixedSizeList as List } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";

interface HistoricoAlteracaoPreco {
    id: number;
    produtoId: number;
    empresaId: number | null;
    empresaNome: string | null;
    data: string;
    valorAnterior: number;
    valorNovo: number;
    origem: OrigemAlteracaoPreco;
    origemDescricao: string;
    usuarioNome: string;
    observacao: string | null;
    diferencaValor: number;
    tipoAlteracao: string;
}

interface HistoricoAlteracaoPrecoResponse {
    data: HistoricoAlteracaoPreco[];
    total: number;
    hasMore: boolean;
}

enum OrigemAlteracaoPreco {
    Cadastro = 0,
    Importacao = 1
}

interface HistoricoAlteracaoPrecoTabProps {
    produtoId: number;
}

interface ItemProps {
    index: number;
    style: React.CSSProperties;
    data: {
        items: HistoricoAlteracaoPreco[];
        loadMoreItems: () => void;
        hasNextPage: boolean;
        isLoading: boolean;
    };
}

const formatMoney = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(value);
};

const formatDifference = (value: number) => {
    const formatted = formatMoney(Math.abs(value));
    if (value > 0) return `+${formatted}`;
    if (value < 0) return `-${formatted}`;
    return formatted;
};

const getDifferenceClass = (value: number) => {
    if (value > 0) return "text-success";
    if (value < 0) return "text-danger";
    return "text-muted";
};

const getIconByOrigem = (origem: OrigemAlteracaoPreco) => {
    switch (origem) {
        case OrigemAlteracaoPreco.Cadastro:
            return <FontAwesomeIcon icon={faEdit} className="text-primary" />;
        case OrigemAlteracaoPreco.Importacao:
            return <FontAwesomeIcon icon={faFileImport} className="text-info" />;
        default:
            return <FontAwesomeIcon icon={faEdit} className="text-muted" />;
    }
};

const getBadgeVariant = (origem: OrigemAlteracaoPreco) => {
    switch (origem) {
        case OrigemAlteracaoPreco.Cadastro:
            return "primary";
        case OrigemAlteracaoPreco.Importacao:
            return "info";
        default:
            return "light";
    }
};

const AlteracaoRow: React.FC<ItemProps> = ({ index, style, data }) => {
    const { items, loadMoreItems, hasNextPage } = data;
    const { breakpoints } = useBreakpoints();
    const isMobile = breakpoints.down('md');

    const isItemLoaded = (index: number) => !!items[index];

    if (!isItemLoaded(index) && hasNextPage) {
        if (index === items.length) {
            loadMoreItems();
            return (
                <div style={style} className="d-flex justify-content-center align-items-center">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Carregando...</span>
                </div>
            );
        }
        return null;
    }

    const alteracao = items[index];
    if (!alteracao) return null;

    if (isMobile) {
        return (
            <div style={style} className="px-3 py-3 border-bottom border-translucent">
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="d-flex align-items-center gap-2">
                        {getIconByOrigem(alteracao.origem)}
                        <Badge bg={getBadgeVariant(alteracao.origem)} pill className="fs-9">
                            {alteracao.origemDescricao}
                        </Badge>
                        {alteracao.tipoAlteracao === "Específico" && (
                            <Badge bg="outline-secondary" pill className="fs-9">
                                Específico
                            </Badge>
                        )}
                    </div>
                    <small className="text-body-tertiary">
                        <FontAwesomeIcon icon={faCalendar} className="me-1" />
                        {new Date(alteracao.data).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                    </small>
                </div>

                <div className="mb-2">
                    <small className="text-body-secondary d-block">Preço de Venda</small>
                    <div>
                        <small className="text-body-tertiary">{formatMoney(alteracao.valorAnterior)} → </small>
                        <span className="fw-bold">{formatMoney(alteracao.valorNovo)}</span>
                        <div className={`fs-9 ${getDifferenceClass(alteracao.diferencaValor)}`}>
                            {formatDifference(alteracao.diferencaValor)}
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-1">
                        {alteracao.empresaNome ? (
                            <>
                                <FontAwesomeIcon icon={faStore} className="text-body-tertiary" size="sm" />
                                <small>{alteracao.empresaNome}</small>
                            </>
                        ) : (
                            <small className="text-info">Global</small>
                        )}
                    </div>
                    <div className="d-flex align-items-center gap-1">
                        <FontAwesomeIcon icon={faUser} className="text-body-tertiary" size="sm" />
                        <small className="text-body-tertiary">{alteracao.usuarioNome}</small>
                    </div>
                </div>

                {alteracao.observacao && (
                    <div className="mt-2">
                        <small className="text-body-tertiary fst-italic">"{alteracao.observacao}"</small>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div style={style} className="d-flex align-items-center border-bottom border-translucent px-3 py-2">
            <div className="flex-shrink-0" style={{ width: "140px" }}>
                <small>
                    {new Date(alteracao.data).toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </small>
            </div>

            <div className="flex-shrink-0 mx-2" style={{ width: "100px" }}>
                <div className="d-flex align-items-center gap-1">
                    {getIconByOrigem(alteracao.origem)}
                    <Badge bg={getBadgeVariant(alteracao.origem)} pill className="fs-9">
                        {alteracao.origemDescricao}
                    </Badge>
                </div>
            </div>

            <div className="flex-shrink-0 mx-2" style={{ width: "120px" }}>
                <div className="d-flex flex-column">
                    <small className="text-body-secondary">Anterior:</small>
                    <span className="fw-bold fs-9">{formatMoney(alteracao.valorAnterior)}</span>
                </div>
            </div>

            <div className="flex-shrink-0 mx-2" style={{ width: "120px" }}>
                <div className="d-flex flex-column">
                    <small className="text-body-secondary">Novo:</small>
                    <span className="fw-bold fs-9">{formatMoney(alteracao.valorNovo)}</span>
                    <span className={`fs-10 ${getDifferenceClass(alteracao.diferencaValor)}`}>
                        {formatDifference(alteracao.diferencaValor)}
                    </span>
                </div>
            </div>

            <div className="flex-grow-1 mx-2">
                <div className="d-flex align-items-center gap-1">
                    {alteracao.empresaNome ? (
                        <>
                            <FontAwesomeIcon icon={faStore} className="text-body-tertiary" size="sm" />
                            <small>{alteracao.empresaNome}</small>
                            <Badge bg="outline-primary" pill className="fs-10 ms-1">
                                Específico
                            </Badge>
                        </>
                    ) : (
                        <Badge bg="info" pill className="fs-9">
                            Global
                        </Badge>
                    )}
                </div>
            </div>

            <div className="flex-shrink-0" style={{ width: "120px" }}>
                <small className="text-body-tertiary">{alteracao.usuarioNome}</small>
            </div>
        </div>
    );
};

const HistoricoAlteracaoPrecoTab: React.FC<HistoricoAlteracaoPrecoTabProps> = ({ produtoId }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { selectedIds: empresaSelecionadas } = useEmpresas();
    const { breakpoints } = useBreakpoints();
    const isMobile = breakpoints.down('md');

    const pageSize = 30;
    const empresasQuery = empresaSelecionadas.join(',');

    const {
        data,
        isLoading,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch
    } = useInfiniteQuery({
        queryKey: ["produto", "historico-alteracao-precos", produtoId, empresasQuery],
        queryFn: async ({ pageParam = 1 }) => {
            const { data } = await axios.get(
                `/api/produto/${produtoId}/historico-alteracao-precos?page=${pageParam}&pageSize=${pageSize}&empresas=${empresasQuery}`
            );
            return data as HistoricoAlteracaoPrecoResponse;
        },
        getNextPageParam: (lastPage: HistoricoAlteracaoPrecoResponse, allPages) => {
            return lastPage.hasMore ? allPages.length + 1 : undefined;
        },
        initialPageParam: 1,
        enabled: isExpanded && !!produtoId,
        staleTime: 5 * 60 * 1000,
    });

    const allAlteracoes = useMemo(() => {
        return data?.pages?.flatMap(page => page.data) || [];
    }, [data]);

    const totalCount = data?.pages?.[0]?.total || 0;

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };

    const loadMoreItems = () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="d-flex justify-content-center p-4">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Carregando histórico...</span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="text-center p-4">
                    <p className="text-danger mb-3">Erro ao carregar histórico de alterações de preço</p>
                    <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => refetch()}
                    >
                        Tentar novamente
                    </button>
                </div>
            );
        }

        if (allAlteracoes.length === 0) {
            return (
                <div className="text-center p-4 text-muted">
                    <FontAwesomeIcon icon={faChartLine} size="2x" className="mb-3" />
                    <p>Nenhuma alteração de preço encontrada</p>
                </div>
            );
        }

        const itemCount = hasNextPage ? allAlteracoes.length + 1 : allAlteracoes.length;

        const tableHeader = (
            <div className="d-flex align-items-center border-bottom border-translucent bg-body-secondary px-3 py-2 fw-bold"
                style={{ marginRight: itemCount > Math.floor(400 / (isMobile ? 140 : 80)) ? "17px" : "0" }}>
                <div style={{ width: "140px" }}>Data</div>
                <div className="mx-2" style={{ width: "100px" }}>Origem</div>
                <div className="mx-2" style={{ width: "120px" }}>Preço Anterior</div>
                <div className="mx-2" style={{ width: "120px" }}>Preço Novo</div>
                <div className="flex-grow-1 mx-2">Escopo</div>
                <div style={{ width: "120px" }}>Usuário</div>
            </div>
        );

        return (
            <>
                {!isMobile && tableHeader}
                <div style={{ height: "400px", border: "1px solid var(--phoenix-border-color)" }}>
                    <InfiniteLoader
                        isItemLoaded={(index) => !!allAlteracoes[index]}
                        itemCount={itemCount}
                        loadMoreItems={loadMoreItems}
                        threshold={10}
                    >
                        {({ onItemsRendered, ref }) => (
                            <List
                                ref={ref}
                                height={400}
                                width="100%"
                                itemCount={itemCount}
                                itemSize={isMobile ? 140 : 80}
                                onItemsRendered={onItemsRendered}
                                itemData={{
                                    items: allAlteracoes,
                                    loadMoreItems,
                                    hasNextPage: !!hasNextPage,
                                    isLoading: isFetchingNextPage
                                }}
                            >
                                {AlteracaoRow}
                            </List>
                        )}
                    </InfiniteLoader>
                </div>

                {totalCount > 0 && (
                    <div className="text-center mt-3">
                        <small className="text-muted">
                            Mostrando {allAlteracoes.length} de {totalCount} alterações
                            {isFetchingNextPage && " • Carregando mais..."}
                        </small>
                    </div>
                )}
            </>
        );
    };

    return (
        <Card className="mb-3">
            <Accordion>
                <Accordion.Item eventKey="0">
                    <Accordion.Header onClick={handleToggle}>
                        <div className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={faChartLine} className="text-warning" />
                            <span>Histórico de Alterações de Preço</span>
                            {totalCount > 0 && (
                                <Badge bg="warning" pill className="ms-2">
                                    {totalCount}
                                </Badge>
                            )}
                        </div>
                    </Accordion.Header>
                    <Accordion.Body>
                        {isExpanded && renderContent()}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </Card>
    );
};

export default HistoricoAlteracaoPrecoTab; 